import { DiagnosticFunnel } from '../../components/tools/DiagnosticFunnel';
import { HowToLd } from '../../components/seo/jsonld/HowTo';
import { BreadcrumbListLd } from '../../components/seo/jsonld/BreadcrumbList';
import { MeshBackground } from '../../components/motion/MeshBackground';
import { Reveal } from '../../components/motion/Reveal';
import { Badge } from '../../components/ui/badge';
import { Stepper } from '../../components/ui/stepper';
import { ToolPageSubHeader } from '../../components/layout/ToolPageLayout';
import { useDiagnostic } from '../../stores/diagnostic';
import { ShieldCheck, Sparkles, Timer, Tractor } from 'lucide-react';
import { cn } from '../../lib/cn';

const STEPS = [
  { id: 'identification', label: 'Profil' },
  { id: 'situation', label: 'Situation' },
  { id: 'energie', label: 'Énergie' },
  { id: 'travaux', label: 'Travaux' },
  { id: 'estimation', label: 'Résultat' },
];

export default function DiagnosticPage() {
  const store = useDiagnostic();
  const isAgri = store.isAgri;

  // Le Stepper visuel à gauche reflète grossièrement la progression :
  // 0 = profil non saisi, 1 = SIRET, 2 = questions, 3 = au moins une réponse,
  // 4 = estimation ou lead obtenu.
  const currentStep = store.leadId || store.estimation
    ? 5
    : store.answers.length > 0
      ? 4
      : store.questions.length > 0
        ? 3
        : store.siret
          ? 2
          : 1;

  return (
    <div className="relative bg-paper">
      <ToolPageSubHeader
        toolLabel={isAgri ? 'Diagnostic primes énergie (CEE) agricole' : 'Diagnostic primes énergie (CEE) en 60 secondes'}
        backHref="/outils"
        backLabel="Tous les outils"
        rightSlot={
          <span className="inline-flex items-center gap-1.5 rounded-full bg-stone-100 px-2.5 py-1 text-[11px] font-semibold text-stone-600">
            <Timer className="h-3 w-3" strokeWidth={2} />
            ~60 sec
          </span>
        }
      />
      <MeshBackground variant="warm" intensity="soft" withGrid />

      {/* Split-screen layout : 40% gauche (info/stepper) / 60% droite (funnel) */}
      <div className="relative flex flex-col lg:flex-row">
        {/* ── Panneau gauche — contexte ── */}
        <div className="relative flex flex-col justify-start border-b border-stone-200/70 bg-paper/80 px-6 pb-8 pt-10 lg:sticky lg:top-0 lg:h-screen lg:w-[40%] lg:overflow-y-auto lg:border-b-0 lg:border-r lg:px-10 lg:pt-16">
          <Reveal>
            <Badge variant={isAgri ? 'accent' : 'ivory'} size="lg" className="gap-2">
              {isAgri ? (
                <><Tractor className="h-3.5 w-3.5" /> Diagnostic primes énergie agricoles</>
              ) : (
                <><Timer className="h-3.5 w-3.5" /> 60 secondes chrono</>
              )}
            </Badge>
            <h1 className={cn(
              'mt-5 font-display text-2xl font-bold leading-tight tracking-tight text-ink md:text-3xl',
            )}>
              {isAgri
                ? 'Quelles aides pour votre exploitation agricole ?'
                : 'Vos primes énergie (CEE) chiffrées en 60 secondes.'}
            </h1>
            <p className="mt-4 text-[15px] leading-relaxed text-stone-600">
              {isAgri
                ? 'SIRET → type d\'exploitation → équipements → estimation des primes CEE agricoles, du PCAE de votre Région et des aides FranceAgriMer. Sans avance sur les chantiers réalisés via notre réseau.'
                : 'Profil → SIRET → situation → estimation chiffrée. Sans inscription, sans engagement.'}
            </p>
            <div className="mt-5 flex flex-col gap-2 text-sm text-stone-500">
              <span className="inline-flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-brand-700" />
                Données publiques officielles
              </span>
              <span className="inline-flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-accent-600" />
                {isAgri ? 'Fiches CEE agricoles AGRI-EQ documentées' : 'Fiches primes énergie (CEE) documentées'}
              </span>
              <span className="inline-flex items-center gap-2">
                <Timer className="h-4 w-4 text-stone-400" />
                Résultat immédiat · sans inscription
              </span>
            </div>
          </Reveal>

          {/* Stepper */}
          <div className="mt-8 hidden lg:block">
            <Stepper
              steps={STEPS}
              currentStep={currentStep - 1}
              orientation="vertical"
            />
          </div>
        </div>

        {/* ── Panneau droit — funnel ── */}
        <div className="flex-1 px-4 py-10 lg:px-12 lg:py-16">
          {/* Stepper mobile */}
          <div className="mb-8 lg:hidden">
            <Stepper steps={STEPS} currentStep={currentStep - 1} orientation="horizontal" />
          </div>
          <Reveal delay={0.1}>
            <DiagnosticFunnel />
          </Reveal>
        </div>
      </div>

        <HowToLd
          name="Diagnostic primes énergie (CEE) en 60 secondes"
          description="Estimer ses primes énergie (CEE) en 5 étapes (profil, SIRET, situation, estimation, confirmation)."
          steps={[
            {
              name: 'Choisir son profil',
              text: "Professionnel, entreprise ou artisan RGE (Reconnu Garant de l'Environnement) : le parcours s'ajuste à votre statut.",
            },
            {
              name: 'Saisie du SIRET',
              text: "On lit votre SIRET sur le registre officiel des entreprises pour pré-remplir vos infos et adapter les questions à votre secteur.",
            },
            {
              name: 'Quelques questions',
              text: "Trois ou quatre questions, taillées pour votre activité et vos projets de travaux.",
            },
            {
              name: 'Estimation chiffrée',
              text: "Une fourchette de primes énergie (CEE) min et max, avec la liste des fiches qui s'appliquent. Calculée sur les barèmes en vigueur.",
            },
            {
              name: 'Plan détaillé par e-mail',
              text: "Pièces à préparer, calendrier, délais de versement. On vous rappelle sous 24h ouvrées.",
            },
          ]}
        />
        <BreadcrumbListLd
          items={[
            { name: 'Accueil', url: 'https://solutionecofrance.fr/' },
            { name: 'Tous les outils', url: 'https://solutionecofrance.fr/outils' },
            { name: 'Diagnostic 60 sec', url: 'https://solutionecofrance.fr/diagnostic' },
          ]}
        />
    </div>
  );
}
